<script setup>
import { useEventsListStore } from '@/js/core/stores/events-list-store.js';
import { useCurrentPageStore } from '@/js/core/stores/current-page-store';
import PageHeading from '@/js/core/components/parts/PageHeading.vue';
import { computed, onMounted, ref, watch } from 'vue';
import EventViewCalendar from '@/js/core/components/parts/calendar/EventViewCalendar.vue';
import CalendarFilters from '@/js/core/components/parts/calendar/CalendarFilters.vue';
import { useCalendarEvents } from '@/js/core/composables/useCalendarEvents.js';
import { useCalendarFilterOptions } from '@/js/core/composables/useCalendarFilterOptions.js';
import { useAcademicCalendarUrlState } from '@/js/core/composables/useAcademicCalendarUrlState.js';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import RightCaretIcon from '@/js/core/components/icons/RightCaretIcon.vue';
import { useTimezoneStore } from '@/js/core/stores/timezone-store';
import { monthToRange } from '@/js/core/utils/month-range.js';
import {
  academicYearMonthRanges,
  academicYearStartForDate,
  academicYearStartForMonthKey,
  semesterForMonthNumber,
  sortDateRanges,
} from '@/js/core/utils/academic-year.js';
import { canAccessFullAcademicYear } from '@/js/core/utils/full-academic-year-access';
import {
  createAcademicCalendarDefaultFilters,
  normalizeCalendarFilters,
} from '@/js/core/utils/calendar-filter-state';
import { eventFilterGroupsFromOptions } from '@/js/core/utils/calendar-events';
import { calendarEventPlacements } from '@/js/core/utils/calendar-event-projections.js';
import { parseAcademicCalendarState } from '@/js/core/utils/calendar-url-state.js';

const eventsStore = useEventsListStore();
const currentPageStore = useCurrentPageStore();
const timezoneStore = useTimezoneStore();
const router = useRouter();

const isLoading = computed(() => eventsStore.isLoading);

const errorMessage = computed(() => eventsStore.error);

const initialUrlState = parseAcademicCalendarState(window.location.search);
const hasInitialMonthSelection = initialUrlState.months.length > 0;

const selectedFilters = ref(
  normalizeCalendarFilters(initialUrlState.filters, createAcademicCalendarDefaultFilters())
);

const currentYear = ref(initialYear(initialUrlState));
const activeView = ref('calendar');

const selectedDateRanges = ref(initialDateRanges(initialUrlState, currentYear.value));

// Which months are visible before clicking 'next month'
const visibleMonths = ref([...selectedDateRanges.value]);

const hasMoreMonths = computed(
  () => selectedDateRanges.value.length !== visibleMonths.value.length
);

const handleDateChange = range => {
  selectedDateRanges.value = range;

  visibleMonths.value = [...range];
};

const showNextMonth = () => {
  // In `visibleMonths`, add the next value from `selectedDateRanges`, which will be at the next index from
  // `visibleMonths`.
  // e.g. if there are 6 months in `currentDateRange` and 3 months are visible, item 4 from `selectedDateRanges` is the
  // next month to make visible.
  visibleMonths.value.push(selectedDateRanges.value.at(visibleMonths.value.length));
};

const { filteredEvents } = useCalendarEvents(
  eventsStore,
  selectedDateRanges,
  selectedFilters,
  true,
  { planning_view: 'full_academic_year' }
);

const { filterGroups, monthOptions, academicYears } = useCalendarFilterOptions(selectedDateRanges, {
  requestParams: { planning_view: 'full_academic_year' },
});

const defaultMonthsApplied = ref(hasInitialMonthSelection);
const eventFilterGroups = computed(() => eventFilterGroupsFromOptions(filterGroups.value));
const projectedEvents = computed(() =>
  calendarEventPlacements(filteredEvents.value, { timezone: timezoneStore.effectiveTimezone })
);

const handleYearChange = year => {
  currentYear.value = year;
};

useAcademicCalendarUrlState({
  selectedYear: currentYear,
  selectedMonths: selectedDateRanges,
  selectedFilters,
});

watch(
  monthOptions,
  options => {
    if (defaultMonthsApplied.value || !Array.isArray(options) || options.length === 0) {
      return;
    }

    const ranges = defaultDateRangesFromMonthOptions(options, currentYear.value);

    if (ranges.length === 0) {
      return;
    }

    selectedDateRanges.value = ranges;
    visibleMonths.value = [...ranges];
    defaultMonthsApplied.value = true;
  },
  { immediate: true }
);

// A URL can carry an academic year the backend doesn't display - if so, snap to the current one.
watch(academicYears, years => {
  if (!years.length || years.includes(currentYear.value)) {
    return;
  }

  currentYear.value = academicYearStartForDate();
  selectedDateRanges.value = academicYearMonthRanges(currentYear.value);
  visibleMonths.value = [...selectedDateRanges.value];
});

onMounted(() => {
  if (!canAccessFullAcademicYear()) {
    router.replace({ name: 'calendar' });
  }
});

onBeforeRouteLeave(() => {
  // Remove extra class from the container (used to allow styling full width)
  // See also `router.js` for the part which adds the class on enter
  document.querySelector('#content-container').classList.remove('full-page');
});

function initialYear(state) {
  if (state.academicYear) {
    return state.academicYear;
  }

  return state.months.length
    ? academicYearStartForMonthKey(state.months[0])
    : academicYearStartForDate();
}

function initialDateRanges(state, year) {
  const ranges = sortDateRanges(
    state.months.map(monthToDateRange).filter(range => rangeInAcademicYear(range, year))
  );

  return ranges.length ? ranges : academicYearMonthRanges(year);
}

function defaultDateRangesFromMonthOptions(options, year) {
  const availableRanges = sortDateRanges(
    options
      .map(option => monthToDateRange(option.slice(0, 7)))
      .filter(range => rangeInAcademicYear(range, year))
  );

  if (availableRanges.length === 0) {
    return [];
  }

  const currentAcademicYear = academicYearStartForDate();
  const preferredSemester = year === currentAcademicYear ? currentSemester() : 'fall';
  const preferredRanges = rangesForSemester(availableRanges, preferredSemester);

  if (preferredRanges.length > 0) {
    return preferredRanges;
  }

  const fallbackRanges = rangesForSemester(availableRanges, semesterForMonth(availableRanges[0]));

  return fallbackRanges.length > 0 ? fallbackRanges : availableRanges;
}

function monthToDateRange(month) {
  const [year, monthNumber] = month.split('-');

  return monthToRange(Number(year), monthNumber);
}

function rangeInAcademicYear(range, startYear) {
  return academicYearStartForMonthKey(range.start_after) === startYear;
}

function rangesForSemester(ranges, semester) {
  return ranges.filter(range => semesterForMonth(range) === semester);
}

function currentSemester() {
  return semesterForMonthNumber(new Date().getMonth() + 1);
}

function semesterForMonth(range) {
  return semesterForMonthNumber(range.start_after.split('-')[1]);
}
</script>

<template>
  <main id="main-content" class="calendar-page-main-content">
    <section id="page-content" class="calendar-page">
      <header class="calendar-page-header">
        <PageHeading
          :title="currentPageStore.title || 'Academic Calendar'"
          :description="currentPageStore.infoContent"
        />
      </header>
      <div class="calendar-page-return-link">
        <a href="/">return to GSD Now</a>
      </div>
      <input
        id="filters-toggle"
        type="checkbox"
        name="toggle"
        value="1"
        class="filters-toggle-checkbox"
        checked
      />
      <div class="filters-toggle-wrapper">
        <div class="filters-toggle-inner">
          <label for="filters-toggle">
            <span class="filters-toggle-icon">
              <RightCaretIcon />
            </span>
            <span>Calendar Settings</span>
          </label>
        </div>
      </div>

      <CalendarFilters
        v-model:view="activeView"
        v-model:filters="selectedFilters"
        :selected-months="selectedDateRanges"
        :academic-years="academicYears"
        :filter-groups="filterGroups"
        :academic-year-link-visible="false"
        :allow-multiple-month-selection="true"
        :month-navigation-visible="false"
        :view-type-visible="false"
        :years-and-months-visible="true"
        :additional-classes="['light-top-border']"
        @year-changed="handleYearChange"
        @months-selection-changed="handleDateChange"
      />

      <div class="calendar-page-content">
        <div v-if="selectedDateRanges.length === 0" class="calendar-page-empty-message">
          Select a Month or Semester to display the Academic Calendar
        </div>
        <div v-else-if="isLoading" class="calendar-page-loading-message">Loading events...</div>
        <div v-else-if="errorMessage">{{ errorMessage }}</div>

        <template v-else>
          <template v-for="dateRange in selectedDateRanges" :key="Object.values(dateRange).join()">
            <EventViewCalendar
              v-if="
                visibleMonths.find(
                  // Compare each item in `visibleMonths` with this `dateRange` and check if both values are the same
                  month =>
                    month.start_after === dateRange.start_after &&
                    month.start_before === dateRange.start_before
                ) !== undefined
              "
              :active-range="dateRange"
              :can-toggle-past-events="false"
              :dim-past-events="false"
              :projected-events="projectedEvents"
              :limit-displayed-events="false"
              :show-past-events="true"
              :always-show-year="true"
              :show-month-name="true"
              :timezone="timezoneStore.effectiveTimezone"
              :event-filter-groups="eventFilterGroups"
            />
          </template>

          <div v-if="hasMoreMonths" class="view-next-month">
            <button type="button" class="view-next-month__link" @click="showNextMonth">
              Next month...
            </button>
          </div>
        </template>
      </div>
    </section>
  </main>
</template>
